import { Stack } from "@mui/material";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { useVideoControlState } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import { Dayjs } from "dayjs";
import { useMemo } from "react";
import { EventCreate, MassTimeSeries } from "src/store/api/generatedApi";
import { ScaleWeightsSingleAreaChart } from "./ScaleWeightsSingleAreaChart";
import {
  getAreasToRender,
  groupScaleWeightSeriesByArea,
} from "./ScaleWeightsChart.utils";

interface ScaleWeightsChartProps {
  data: MassTimeSeries;
  videoStart: Dayjs;
  events?: ScrapEyesEvent[];
  candidateEvents?: EventCreate[];
}

const EMPTY_EVENTS: ScrapEyesEvent[] = [];
const EMPTY_CANDIDATES: EventCreate[] = [];

// 2 minutes total window, current time in center
const WINDOW_DURATION_SECONDS = 120;
const HALF_WINDOW_SECONDS = WINDOW_DURATION_SECONDS / 2;

export const ScaleWeightsChart = ({
  data,
  videoStart,
  events = EMPTY_EVENTS,
  candidateEvents = EMPTY_CANDIDATES,
}: ScaleWeightsChartProps) => {
  const state = useVideoControlState();
  const {
    drop_areas: dropAreas,
    mass_area_matching: massAreaMatching,
    scale_reading_lookup: scaleReadingLookup,
  } = useScrapEyeMetadata();

  const currentTimestamp = videoStart.add(state.currentTime, "second");
  const visibleStart = currentTimestamp.subtract(HALF_WINDOW_SECONDS, "second");
  const visibleEnd = currentTimestamp.add(HALF_WINDOW_SECONDS, "second");

  const showTimeMarker = state.duration > 0;

  const { grouped: groupedData, orderedAreas: areasWithScaleWeights } = useMemo(
    () => groupScaleWeightSeriesByArea(data.series),
    [data.series]
  );

  const areasToRender = useMemo(
    () => getAreasToRender({ areasWithScaleWeights, dropAreas }),
    [areasWithScaleWeights, dropAreas]
  );

  return (
    <Stack sx={{ width: "100%", mx: -0.5, overflow: "visible" }} spacing={0}>
      {areasToRender.map((scrapCar, index) => (
        <ScaleWeightsSingleAreaChart
          key={scrapCar}
          scrapCar={scrapCar}
          areaIndex={index}
          seriesData={groupedData[scrapCar]}
          events={events}
          candidateEvents={candidateEvents}
          massAreaMatching={massAreaMatching}
          scaleReadingLookup={scaleReadingLookup}
          currentTimestamp={currentTimestamp}
          visibleStart={visibleStart}
          visibleEnd={visibleEnd}
          showTimeMarker={showTimeMarker}
          isLast={index === areasToRender.length - 1}
        />
      ))}
    </Stack>
  );
};
